'use client';

import React, { useState, useEffect, useRef } from 'react';
import { useChat } from '@/lib/hooks/useChat';
import { toast } from 'sonner';
import { BookOpen, HelpCircle, Lightbulb, Calendar, Send, RotateCcw } from 'lucide-react';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

const SYSTEM_MESSAGE = {
  role: 'system' as const,
  content: `You are REUSA's AI Study Assistant — an expert academic tutor for the Real Estate University of South Africa. 
You help students with:
- Real estate law, property valuation, and investment analysis
- Course content, assignments, and exam preparation
- Study strategies and academic support
- South African property market insights and regulations
- Career guidance in real estate and property management
- Summarising lessons and course content clearly
- Generating practice quizzes with multiple-choice questions
- Explaining difficult concepts in simple terms
- Creating personalised study plans

When generating a quiz, format it with numbered questions and lettered options (A, B, C, D). 
When creating a study plan, format it as a structured weekly schedule.
When summarising, use clear headings and bullet points.

Be concise, professional, and encouraging. Use clear explanations with practical examples relevant to the South African real estate context.`,
};

interface QuickAction {
  id: string;
  icon: React.ElementType;
  label: string;
  description: string;
  prompt: string;
  color: string;
}

const QUICK_ACTIONS: QuickAction[] = [
  {
    id: 'summarise',
    icon: BookOpen,
    label: 'Summarise Lesson',
    description: 'Get a clear summary of any topic',
    prompt: 'Please summarise the key concepts of property valuation methods used in South Africa, including the income approach, cost approach, and sales comparison approach.',
    color: 'emerald',
  },
  {
    id: 'quiz',
    icon: HelpCircle,
    label: 'Generate Quiz',
    description: 'Test your knowledge with practice questions',
    prompt: 'Generate a 5-question multiple-choice quiz on South African property law, including the Deeds Registries Act, Sectional Titles Act, and the Property Practitioners Act. Include the correct answers at the end.',
    color: 'blue',
  },
  {
    id: 'explain',
    icon: Lightbulb,
    label: 'Explain Concept',
    description: 'Break down difficult concepts simply',
    prompt: 'Explain the concept of Net Present Value (NPV) in property investment in simple terms, with a practical South African example.',
    color: 'amber',
  },
  {
    id: 'studyplan',
    icon: Calendar,
    label: 'Study Plan',
    description: 'Create a personalised study schedule',
    prompt: 'Create a 4-week study plan for a student preparing for their Property Development exam. Include daily study goals, key topics to cover each week, and revision strategies.',
    color: 'purple',
  },
];

const SUGGESTED_QUESTIONS = [
  'Explain the Sectional Titles Act',
  'How do I calculate property ROI?',
  'What is conveyancing in SA?',
  'Help me study for my valuation exam',
];

const actionColorMap: Record<string, { bg: string; border: string; text: string; badge: string }> = {
  emerald: { bg: 'bg-emerald-900/30', border: 'border-emerald-700/30', text: 'text-emerald-400', badge: 'bg-emerald-900/50 border-emerald-700/30' },
  blue: { bg: 'bg-blue-900/30', border: 'border-blue-700/30', text: 'text-blue-400', badge: 'bg-blue-900/50 border-blue-700/30' },
  amber: { bg: 'bg-amber-900/30', border: 'border-amber-700/30', text: 'text-amber-400', badge: 'bg-amber-900/50 border-amber-700/30' },
  purple: { bg: 'bg-purple-900/30', border: 'border-purple-700/30', text: 'text-purple-400', badge: 'bg-purple-900/50 border-purple-700/30' },
};

export default function AIStudyAssistant() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [streamingContent, setStreamingContent] = useState('');
  const [activeAction, setActiveAction] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const { response, isLoading, error, sendMessage } = useChat('OPEN_AI', 'gpt-4o', true);

  useEffect(() => {
    if (error) toast.error(error.message);
  }, [error]);

  useEffect(() => {
    if (isLoading) {
      setStreamingContent(response || '');
    } else if (response && !isLoading) {
      setStreamingContent('');
      setMessages(prev => {
        const last = prev[prev.length - 1];
        if (last?.role === 'assistant' && last.content === response) return prev;
        const withoutLastAssistant =
          last?.role === 'assistant' ? prev.slice(0, -1) : prev;
        return [...withoutLastAssistant, { role: 'assistant', content: response }];
      });
      setActiveAction(null);
    }
  }, [response, isLoading]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingContent]);

  const handleSend = (text?: string) => {
    const messageText = text ?? input.trim();
    if (!messageText || isLoading) return;

    const newUserMessage: Message = { role: 'user', content: messageText };
    const updatedMessages = [...messages, newUserMessage];
    setMessages(updatedMessages);
    setInput('');

    const apiMessages = [
      SYSTEM_MESSAGE,
      ...updatedMessages.map(m => ({ role: m.role, content: m.content })),
    ];

    sendMessage(apiMessages, { max_completion_tokens: 1500 });
  };

  const handleQuickAction = (action: QuickAction) => {
    setActiveAction(action.id);
    handleSend(action.prompt);
  };

  const handleClearChat = () => {
    setMessages([]);
    setStreamingContent('');
    setActiveAction(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <section className="relative py-24 bg-[#0a1a12] overflow-hidden">
      {/* Atmospheric background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-emerald-900/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-emerald-800/15 rounded-full blur-3xl" />
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              'repeating-linear-gradient(0deg, transparent, transparent 40px, rgba(255,255,255,0.1) 40px, rgba(255,255,255,0.1) 41px), repeating-linear-gradient(90deg, transparent, transparent 40px, rgba(255,255,255,0.1) 40px, rgba(255,255,255,0.1) 41px)',
          }}
        />
      </div>

      <div className="relative max-w-screen-xl mx-auto px-6 lg:px-10 xl:px-16">
        {/* Header */}
        <div className="mb-10 max-w-2xl">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-700/40 bg-emerald-900/30 mb-5">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-emerald-400 text-xs font-medium tracking-widest uppercase font-sans">
              AI-Powered Learning
            </span>
          </div>
          <h2 className="font-serif text-4xl lg:text-5xl font-bold text-white leading-tight mb-4">
            Your Personal
            <br />
            <span className="text-emerald-400">Study Assistant</span>
          </h2>
          <p className="text-white/60 font-sans text-base leading-relaxed">
            Summarise lessons, generate quizzes, explain concepts, and build study plans — powered by AI.
          </p>
        </div>

        {/* Quick Action Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
          {QUICK_ACTIONS.map((action) => {
            const colors = actionColorMap[action.color];
            const isActive = activeAction === action.id && isLoading;
            return (
              <button
                key={action.id}
                onClick={() => handleQuickAction(action)}
                disabled={isLoading}
                className={`flex flex-col items-start gap-2.5 p-4 rounded-xl border text-left transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${colors.bg} ${colors.border} hover:brightness-110`}
              >
                <div className={`w-8 h-8 rounded-lg border flex items-center justify-center ${colors.badge}`}>
                  {isActive ? (
                    <span className={`w-3 h-3 rounded-full ${colors.text} animate-pulse`} style={{ background: 'currentColor' }} />
                  ) : (
                    <action.icon size={15} className={colors.text} />
                  )}
                </div>
                <div>
                  <p className={`font-sans text-sm font-semibold ${colors.text}`}>{action.label}</p>
                  <p className="text-white/40 font-sans text-xs leading-relaxed mt-0.5">{action.description}</p>
                </div>
              </button>
            );
          })}
        </div>

        {/* Chat Interface */}
        <div className="grid lg:grid-cols-[1fr_300px] gap-6 items-start">
          {/* Main chat panel */}
          <div className="flex flex-col rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm overflow-hidden"
            style={{ minHeight: '480px', maxHeight: '560px' }}>
            {/* Chat header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <span className="text-white/60 font-sans text-xs font-medium tracking-wide">REUSA AI Tutor</span>
              </div>
              {messages.length > 0 && (
                <button
                  onClick={handleClearChat}
                  className="flex items-center gap-1.5 text-white/30 hover:text-white/60 transition-colors font-sans text-xs"
                >
                  <RotateCcw size={11} />
                  Clear
                </button>
              )}
            </div>

            {/* Messages area */}
            <div className="flex-1 overflow-y-auto p-5 space-y-4" style={{ minHeight: 0 }}>
              {messages.length === 0 && !isLoading && (
                <div className="flex flex-col items-center justify-center h-full text-center py-10">
                  <div className="w-14 h-14 rounded-2xl bg-emerald-900/50 border border-emerald-700/30 flex items-center justify-center mb-4">
                    <svg className="w-7 h-7 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                        d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" />
                    </svg>
                  </div>
                  <p className="text-white/50 font-sans text-sm font-medium mb-1">Ask anything or use a quick action above</p>
                  <p className="text-white/25 font-sans text-xs">Summarise lessons · Generate quizzes · Explain concepts · Study plans</p>
                </div>
              )}

              {messages.map((msg, i) => (
                <div
                  key={i}
                  className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {msg.role === 'assistant' && (
                    <div className="w-7 h-7 rounded-lg bg-emerald-800/60 border border-emerald-700/40 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-emerald-400 text-xs font-bold font-sans">AI</span>
                    </div>
                  )}
                  <div
                    className={`max-w-[82%] rounded-xl px-4 py-3 font-sans text-sm leading-relaxed whitespace-pre-wrap ${
                      msg.role === 'user' ?'bg-emerald-700/40 text-white border border-emerald-600/30' :'bg-white/[0.06] text-white/85 border border-white/10'
                    }`}
                  >
                    {msg.content}
                  </div>
                  {msg.role === 'user' && (
                    <div className="w-7 h-7 rounded-lg bg-white/10 border border-white/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg className="w-3.5 h-3.5 text-white/60" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" />
                      </svg>
                    </div>
                  )}
                </div>
              ))}

              {/* Streaming message */}
              {isLoading && streamingContent && (
                <div className="flex gap-3 justify-start">
                  <div className="w-7 h-7 rounded-lg bg-emerald-800/60 border border-emerald-700/40 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-emerald-400 text-xs font-bold font-sans">AI</span>
                  </div>
                  <div className="max-w-[82%] rounded-xl px-4 py-3 bg-white/[0.06] text-white/85 border border-white/10 font-sans text-sm leading-relaxed whitespace-pre-wrap">
                    {streamingContent}
                    <span className="inline-block w-1.5 h-4 bg-emerald-400 ml-0.5 animate-pulse align-middle" />
                  </div>
                </div>
              )}

              {/* Loading dots when no content yet */}
              {isLoading && !streamingContent && (
                <div className="flex gap-3 justify-start">
                  <div className="w-7 h-7 rounded-lg bg-emerald-800/60 border border-emerald-700/40 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-emerald-400 text-xs font-bold font-sans">AI</span>
                  </div>
                  <div className="rounded-xl px-4 py-3 bg-white/[0.06] border border-white/10 flex gap-1.5 items-center">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400/60 animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400/60 animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400/60 animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input area */}
            <div className="border-t border-white/10 p-4">
              <div className="flex gap-3 items-end">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask about property law, valuation, exam prep..."
                  disabled={isLoading}
                  rows={1}
                  className="flex-1 bg-white/[0.06] border border-white/15 rounded-xl px-4 py-3 text-white placeholder-white/30 font-sans text-sm resize-none focus:outline-none focus:border-emerald-600/60 transition-colors disabled:opacity-50"
                  style={{ minHeight: '44px', maxHeight: '120px' }}
                />
                <button
                  onClick={() => handleSend()}
                  disabled={isLoading || !input.trim()}
                  className="w-11 h-11 rounded-xl bg-emerald-700 hover:bg-emerald-600 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center transition-colors flex-shrink-0"
                >
                  <Send size={15} className="text-white" />
                </button>
              </div>
              <p className="text-white/25 font-sans text-xs mt-2 pl-1">
                Press Enter to send · Shift+Enter for new line
              </p>
            </div>
          </div>

          {/* Sidebar: suggested questions + knowledge areas */}
          <div className="space-y-4">
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
              <p className="text-white/50 font-sans text-xs uppercase tracking-widest mb-4">
                Suggested Topics
              </p>
              <div className="space-y-2">
                {SUGGESTED_QUESTIONS.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => handleSend(q)}
                    disabled={isLoading}
                    className="w-full text-left px-4 py-3 rounded-xl border border-white/10 bg-white/[0.03] hover:bg-emerald-900/30 hover:border-emerald-700/40 text-white/70 hover:text-white font-sans text-sm transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 space-y-4">
              <p className="text-white/50 font-sans text-xs uppercase tracking-widest mb-1">Knowledge Areas</p>
              {[
                { icon: '📚', label: 'Course Content', desc: 'All REUSA programmes' },
                { icon: '⚖️', label: 'SA Property Law', desc: 'Acts, regulations & cases' },
                { icon: '📊', label: 'Valuation Methods', desc: 'DCF, comparable sales & more' },
              ].map((item, i) => (
                <div key={i} className="flex gap-3 items-start">
                  <span className="text-lg leading-none mt-0.5">{item.icon}</span>
                  <div>
                    <p className="text-white/80 font-sans text-sm font-medium">{item.label}</p>
                    <p className="text-white/40 font-sans text-xs">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
